/**
 * Subscription — Phase 12.
 *
 * Page dédiée `/abonnement`. Deux plans (mensuel 24 €/mois, annuel 240 €/an
 * avec 2 mois offerts). Les CTA ouvrent les Payment Links Stripe (hébergés
 * sur `buy.stripe.com`, compte live du client) dans un nouvel onglet avec
 * les query params standards Stripe :
 *   - client_reference_id → user_id (pour la réconciliation webhook)
 *   - prefilled_email → email connecté
 *
 * L'état d'abonnement actuel est chargé depuis `GET /api/subscription/me`.
 * Aucun paywall n'est appliqué à ce stade — on pose seulement les fondations.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, ArrowRight, Check, ExternalLink, Loader2, ShieldCheck, Sparkles, Gift,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

// Legacy live-links mode: customer's OWN buy.stripe.com Payment Links.
const PAYMENT_LINKS = {
    monthly: "https://buy.stripe.com/dRmeVe2zZbEs5Ndf4Sebu0j",
    annual: "https://buy.stripe.com/aFaeVe5MbgYMcbB2i6ebu0i",
};

// Stripe price lookup_keys for the managed sandbox flow (see stripe_catalog.py).
const LOOKUP_KEYS = {
    monthly: "lexia_monthly_eur",
    annual: "lexia_annual_eur",
};

/** Standard Stripe Payment Link query params: client_reference_id + prefilled_email.
 *  Documented at https://docs.stripe.com/payment-links/customize
 */
function buildPaymentUrl(base, user) {
    const url = new URL(base);
    if (user?.id) url.searchParams.set("client_reference_id", user.id);
    if (user?.email) url.searchParams.set("prefilled_email", user.email);
    return url.toString();
}

const BENEFITS = [
    "Leçon quotidienne calibrée CECRL, 10 étapes guidées",
    "Révisions par répétition espacée (paliers 10 min → 30 j)",
    "Conversation IA multi-scénarios + bilan structuré",
    "Coach vocale Lexia (WebRTC temps réel)",
    "Podcasts sur mesure + quiz d'écoute + résumé oral",
    "Mode intensif 50/100 mots par jour, tests de bloc",
    "Parcours professionnels (Médical, Business, etc.)",
    "Certificats téléchargeables (PNG/PDF) + vérification publique",
    "Cartes de progression partageables (LinkedIn, Twitter)",
    "6 voix TTS sélectionnables · rythme ralenti",
];

function planLabel(plan) {
    if (plan === "monthly") return "Mensuel · 24 €/mois";
    if (plan === "annual") return "Annuel · 240 €/an";
    if (plan === "lifetime_free") return "Accès complet offert";
    return "Gratuit";
}

function formatDateFr(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
    } catch {
        return null;
    }
}

export default function Subscription() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [sub, setSub] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState(null); // { mode, is_test_mode } | null
    const [checkoutLoading, setCheckoutLoading] = useState(null); // "monthly" | "annual" | null

    useEffect(() => {
        (async () => {
            try {
                const [{ data: subData }, { data: modeData }] = await Promise.all([
                    api.get("/subscription/me"),
                    api.get("/subscription/mode"),
                ]);
                setSub(subData || null);
                setMode(modeData || null);
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
        // Intentional mount-only fetch — the subscription is refreshed on
        // subsequent navigations. Adding deps would spam /subscription/me.
    }, []);

    const isManaged = mode?.mode === "managed_sandbox";
    // Test banner removed per user request. Sandbox status is documented in PRD;
    // real cards will be declined until the Stripe account is claimed (KYC).

    async function startManagedCheckout(planKey) {
        setCheckoutLoading(planKey);
        try {
            const { data } = await api.post("/stripe/checkout", {
                lookup_key: LOOKUP_KEYS[planKey],
                origin_url: window.location.origin,
            });
            if (data?.checkout_url) {
                window.location.assign(data.checkout_url);
            } else {
                toast.error("Réponse Stripe inattendue — impossible de rediriger.");
                setCheckoutLoading(null);
            }
        } catch (err) {
            toast.error(formatApiError(err));
            setCheckoutLoading(null);
        }
    }

    const monthlyUrl = useMemo(() => buildPaymentUrl(PAYMENT_LINKS.monthly, user), [user]);
    const annualUrl = useMemo(() => buildPaymentUrl(PAYMENT_LINKS.annual, user), [user]);

    const currentPlan = sub?.plan || "free";
    const isActive = !!sub?.is_active;
    const isLifetimeFree = currentPlan === "lifetime_free";
    const renewalDate = formatDateFr(sub?.current_period_end);

    return (
        <div className="min-h-screen bg-paper" data-testid="page-subscription">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
                    <button
                        onClick={() => navigate(-1)}
                        className="text-inkSoft hover:text-ink text-sm inline-flex items-center gap-1 mb-4"
                        data-testid="subscription-back"
                    >
                        <ArrowLeft className="w-4 h-4" /> Retour
                    </button>

                    <p className="text-[11px] tracking-[0.28em] uppercase text-inkSoft mb-3">Abonnement</p>
                    <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-ink">
                        Débloquez la totalité de <span className="italic text-terracotta">Lexia School</span>.
                    </h1>
                    <p className="mt-4 text-base text-inkSoft max-w-2xl">
                        Un seul abonnement pour tout : leçons, mode intensif, conversation IA, coach vocale,
                        podcasts, parcours pros, certificats. Aucun crédit horaire, aucune surprise.
                    </p>

                    {/* Current status band */}
                    <div
                        className="mt-8 rounded-2xl border border-lexiBorder bg-paperAlt p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-6"
                        data-testid="subscription-status"
                    >
                        <div className="flex items-center gap-3">
                            <div className={`w-2.5 h-2.5 rounded-full ${isActive ? "bg-emerald" : "bg-inkSoft/40"}`} />
                            <div>
                                <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft">Votre plan actuel</p>
                                <p className="font-serif text-lg text-ink" data-testid="subscription-current-plan">
                                    {loading ? "…" : planLabel(currentPlan)}
                                    {isActive && sub?.status && (
                                        <span className="ml-2 text-xs text-inkSoft uppercase tracking-[0.18em]">
                                            {sub.status}
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                        {renewalDate && (
                            <p className="text-sm text-inkSoft md:ml-auto" data-testid="subscription-renewal">
                                {sub?.cancel_at_period_end ? "Se termine le" : "Prochain renouvellement"}
                                {" "}<span className="text-ink font-medium">{renewalDate}</span>
                            </p>
                        )}
                    </div>

                    {/* Plans OR Lifetime Free panel */}
                    {isLifetimeFree ? (
                        <div
                            className="mt-8 rounded-3xl border-2 border-emerald bg-white p-8 md:p-10"
                            data-testid="subscription-lifetime-panel"
                        >
                            <div className="flex items-center gap-3 mb-3">
                                <Gift className="w-8 h-8 text-emerald" strokeWidth={2} />
                                <h2 className="font-serif text-3xl text-ink" data-testid="subscription-lifetime-title">
                                    Accès complet offert
                                </h2>
                            </div>
                            <p className="text-sm uppercase tracking-[0.28em] text-emerald font-semibold mb-4">
                                Compte partenaire
                            </p>
                            <p className="text-base text-ink mb-2 leading-relaxed">
                                Votre compte bénéficie d&apos;un <strong>accès complet à vie</strong> à Lexia School.
                                Aucun paiement, aucune limitation, aucune date d&apos;expiration.
                            </p>
                            <p className="text-sm text-inkSoft mb-6 leading-relaxed">
                                Toutes les fonctionnalités sont débloquées : leçons quotidiennes, mode intensif,
                                révisions SRS, conversation IA, coach vocale Lexia, podcasts, parcours professionnels,
                                certificats. Merci pour votre soutien.
                            </p>
                            <button
                                type="button"
                                onClick={() => navigate("/dashboard")}
                                className="inline-flex items-center gap-2 bg-ink hover:bg-emerald text-white rounded-full px-5 py-3 text-sm font-medium transition-colors"
                                data-testid="subscription-lifetime-dashboard"
                            >
                                Aller au tableau de bord
                                <ArrowRight className="w-4 h-4" strokeWidth={2} />
                            </button>
                        </div>
                    ) : (
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                        {/* Monthly */}
                        <PlanCard
                            testid="plan-monthly"
                            eyebrow="Mensuel"
                            price="24 €"
                            cadence="/ mois"
                            baseline="Sans engagement, résiliable à tout moment."
                            ctaLabel="S'abonner au mensuel"
                            ctaHref={isManaged ? null : monthlyUrl}
                            onCtaClick={isManaged ? () => startManagedCheckout("monthly") : null}
                            ctaLoading={checkoutLoading === "monthly"}
                            ctaTestid="cta-monthly"
                            highlighted={false}
                            currentPlan={currentPlan === "monthly"}
                            note={isManaged
                                ? "Paiement sécurisé via Stripe Checkout"
                                : "Facturation mensuelle · Payment Link Stripe sécurisé"}
                        />

                        {/* Annual — featured */}
                        <PlanCard
                            testid="plan-annual"
                            eyebrow="Annuel"
                            price="240 €"
                            cadence="/ an"
                            baseline="12 mois pour le prix de 10 · vous économisez 48 €."
                            savingsBadge="2 mois offerts"
                            ctaLabel="S'abonner à l'annuel"
                            ctaHref={isManaged ? null : annualUrl}
                            onCtaClick={isManaged ? () => startManagedCheckout("annual") : null}
                            ctaLoading={checkoutLoading === "annual"}
                            ctaTestid="cta-annual"
                            highlighted={true}
                            currentPlan={currentPlan === "annual"}
                            note={isManaged
                                ? "Paiement unique · Stripe Checkout"
                                : "Paiement unique · Payment Link Stripe sécurisé"}
                            strikethrough="288 €"
                        />
                    </div>
                    )}

                    {/* Benefits */}
                    <section className="mt-12" data-testid="subscription-benefits">
                        <p className="text-[11px] tracking-[0.28em] uppercase text-inkSoft mb-3">Ce qui est inclus</p>
                        <h2 className="font-serif text-2xl md:text-3xl text-ink mb-6">
                            Tout Lexia School, sans limite.
                        </h2>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                            {BENEFITS.map((b, i) => (
                                <li key={i} className="flex items-start gap-3 text-[15px]" data-testid={`benefit-${i}`}>
                                    <Check className="w-4 h-4 text-emerald mt-1 shrink-0" strokeWidth={2.5} />
                                    <span className="text-ink">{b}</span>
                                </li>
                            ))}
                        </ul>
                    </section>

                    {/* Trust — hidden for lifetime_free (no payment happening) */}
                    {!isLifetimeFree && (
                    <section className="mt-12 rounded-2xl border border-lexiBorder bg-white p-5 md:p-6 flex items-start gap-4">
                        <ShieldCheck className="w-5 h-5 text-emerald mt-1 shrink-0" strokeWidth={2} />
                        <div className="text-sm text-inkSoft leading-relaxed">
                            <p className="text-ink font-medium mb-1">Paiement sécurisé, réconciliation automatique.</p>
                            <p>
                                Vous êtes redirigé·e vers une page <strong>Stripe</strong> hébergée
                                (<code className="text-xs bg-paperAlt border border-lexiBorder rounded px-1">
                                    {isManaged ? "checkout.stripe.com" : "buy.stripe.com"}
                                </code>).
                                Aucune donnée bancaire ne transite par Lexia School.
                                {isManaged
                                    ? " Après paiement, vous revenez automatiquement sur Lexia School et votre plan est activé dans les secondes qui suivent."
                                    : " Après paiement, votre compte est automatiquement passé au bon plan grâce à un identifiant de session Stripe transmis dans le lien."}
                            </p>
                            <p className="mt-2">
                                Pour toute question, écrivez à{" "}
                                <a
                                    href="mailto:support@openmeta.fr"
                                    className="text-terracotta hover:underline"
                                    data-testid="subscription-support-email"
                                >
                                    support@openmeta.fr
                                </a>.
                            </p>
                        </div>
                    </section>
                    )}
            </div>
        </div>
    );
}

function PlanCard({
    testid, eyebrow, price, cadence, baseline, savingsBadge, ctaLabel, ctaHref, onCtaClick, ctaLoading, ctaTestid,
    highlighted, currentPlan, note, strikethrough,
}) {
    const cardClass = highlighted
        ? "relative rounded-3xl border-2 border-ink bg-white p-6 md:p-8 shadow-sm"
        : "relative rounded-3xl border border-lexiBorder bg-white p-6 md:p-8";
    const ctaClass = highlighted
        ? "w-full inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-5 py-3 text-sm font-medium transition-colors disabled:opacity-70 disabled:cursor-wait"
        : "w-full inline-flex items-center justify-center gap-2 bg-ink hover:bg-emerald text-white rounded-full px-5 py-3 text-sm font-medium transition-colors disabled:opacity-70 disabled:cursor-wait";
    return (
        <div className={cardClass} data-testid={testid}>
            {highlighted && (
                <span
                    className="absolute -top-3 left-6 bg-emerald text-white text-[10px] tracking-[0.24em] uppercase font-semibold px-3 py-1 rounded-full inline-flex items-center gap-1"
                    data-testid="plan-annual-featured"
                >
                    <Sparkles className="w-3 h-3" strokeWidth={2.5} />
                    Le plus choisi
                </span>
            )}
            <p className="text-[11px] tracking-[0.28em] uppercase text-inkSoft">{eyebrow}</p>
            <div className="mt-3 flex items-baseline gap-2">
                <span className="font-serif text-5xl text-ink">{price}</span>
                <span className="text-sm text-inkSoft">{cadence}</span>
                {strikethrough && (
                    <span className="text-sm text-inkSoft line-through ml-1" data-testid={`${testid}-strike`}>
                        {strikethrough}
                    </span>
                )}
            </div>
            {savingsBadge && (
                <p className="mt-2">
                    <span
                        className="inline-flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase font-semibold text-emerald bg-emerald/10 border border-emerald/40 rounded-full px-2.5 py-1"
                        data-testid={`${testid}-savings-badge`}
                    >
                        {savingsBadge}
                    </span>
                </p>
            )}
            <p className="mt-4 text-sm text-inkSoft leading-relaxed">{baseline}</p>

            <div className="mt-6">
                {currentPlan ? (
                    <div
                        className="w-full inline-flex items-center justify-center gap-2 bg-emerald/10 border border-emerald text-emerald rounded-full px-5 py-3 text-sm font-medium"
                        data-testid={`${ctaTestid}-current`}
                    >
                        <Check className="w-4 h-4" strokeWidth={2.5} /> Votre plan actuel
                    </div>
                ) : onCtaClick ? (
                    <button
                        type="button"
                        onClick={onCtaClick}
                        disabled={ctaLoading}
                        className={ctaClass}
                        data-testid={ctaTestid}
                    >
                        {ctaLoading ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Redirection…
                            </>
                        ) : (
                            <>
                                {ctaLabel}
                                <ArrowRight className="w-4 h-4" strokeWidth={2} />
                            </>
                        )}
                    </button>
                ) : (
                    <a
                        href={ctaHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={ctaClass}
                        data-testid={ctaTestid}
                    >
                        {ctaLabel}
                        <ArrowRight className="w-4 h-4" strokeWidth={2} />
                        <ExternalLink className="w-3 h-3 opacity-60" strokeWidth={2} />
                    </a>
                )}
            </div>
            <p className="mt-3 text-[11px] text-inkSoft text-center">{note}</p>
        </div>
    );
}
